import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API, formatPrice } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Package, Truck, CheckCircle2, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import { STORE_NAME } from "@/lib/seo";

const STATUS_STEPS = [
    { key: "pending", label: "Received", Icon: Clock },
    { key: "confirmed", label: "Confirmed", Icon: CheckCircle2 },
    { key: "packed", label: "Packed", Icon: Package },
    { key: "shipped", label: "Shipped", Icon: Truck },
    { key: "delivered", label: "Delivered", Icon: CheckCircle2 },
];

export default function OrderTracking() {
    const { settings } = useSettings();
    const cur = settings?.currency_symbol || "Rs.";
    const [params] = useSearchParams();
    const [orderNumber, setOrderNumber] = useState(params.get("n") || "");
    const [phone, setPhone] = useState(params.get("p") || "");
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const lookup = async (e) => {
        e?.preventDefault?.();
        if (!orderNumber || !phone) return;
        setLoading(true);
        setError("");
        try {
            // Use plain fetch WITHOUT credentials — this is a public endpoint and
            // including credentials breaks cross-origin requests when the ingress
            // returns Access-Control-Allow-Origin:* (which it does for public APIs).
            const url =
                `${API}/store/orders/track?order_number=${encodeURIComponent(orderNumber.trim())}` +
                `&phone=${encodeURIComponent(phone.trim())}`;
            const res = await fetch(url, {
                method: "GET",
                credentials: "omit",
                headers: { Accept: "application/json" },
            });
            if (res.ok) {
                const data = await res.json();
                setOrder(data);
            } else if (res.status === 404 || res.status === 400) {
                let detail = "";
                try {
                    detail = (await res.json())?.detail || "";
                } catch (_) {}
                setOrder(null);
                setError(detail || "Order not found. Please check order number and phone.");
            } else {
                setOrder(null);
                setError("Unable to check order right now. Please try again.");
            }
        } catch (err) {
            setOrder(null);
            setError("Unable to check order right now. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (orderNumber && phone) lookup();
        // eslint-disable-next-line
    }, []);

    const stepIndex = order ? STATUS_STEPS.findIndex((s) => s.key === order.status) : -1;

    return (
        <div className="container mx-auto px-4 py-8 md:py-12 max-w-3xl" data-testid="track-order-page">
            <SEO title={`Track Order | ${STORE_NAME}`} description="Track your fishing tackle order from Opensea Fishing Tackles." noindex />
            <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight">Track order</h1>
            <p className="text-white/55 mt-1">Enter your order number and phone to view status.</p>

            <form onSubmit={lookup} className="mt-6 grid md:grid-cols-[1fr,1fr,auto] gap-3">
                <input
                    placeholder="Order number (e.g., OST...)"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    className="bg-[#121214] border border-[#27272A] rounded-sm px-3 py-3 focus:border-[#00BCE5] outline-none"
                    data-testid="track-order-number"
                />
                <input
                    placeholder="Phone used at checkout"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="bg-[#121214] border border-[#27272A] rounded-sm px-3 py-3 focus:border-[#00BCE5] outline-none"
                    data-testid="track-phone"
                />
                <button
                    type="submit"
                    disabled={loading}
                    className="bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-[#33C9EA] disabled:opacity-50"
                    data-testid="track-submit"
                >
                    {loading ? "Loading…" : "Track"}
                </button>
            </form>

            {error && <div className="mt-4 text-[#FF3B30] text-sm" data-testid="track-error">{error}</div>}

            {order && (
                <div className="mt-8 bg-[#121214] border border-[#27272A] rounded-sm p-5" data-testid="track-result">
                    <div className="flex flex-wrap items-center gap-3 justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-white/55 font-display">Order #</div>
                            <div className="font-display font-black text-xl">{order.order_number}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs uppercase tracking-widest text-white/55 font-display">Status</div>
                            <div className="font-display font-black text-[#00BCE5] uppercase">{order.status}</div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="mt-6 grid grid-cols-5 gap-2">
                        {STATUS_STEPS.map((s, i) => {
                            const reached = stepIndex >= i && order.status !== "cancelled";
                            const Icon = s.Icon;
                            return (
                                <div key={s.key} className="flex flex-col items-center text-center">
                                    <div
                                        className={`w-9 h-9 rounded-full grid place-items-center border ${
                                            reached
                                                ? "border-[#00BCE5] text-[#00BCE5] bg-[#00BCE5]/10"
                                                : "border-[#27272A] text-white/40"
                                        }`}
                                    >
                                        <Icon size={16} />
                                    </div>
                                    <div className={`mt-1 text-[10px] uppercase tracking-widest font-display ${reached ? "text-white" : "text-white/35"}`}>
                                        {s.label}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {order.tracking_number && (
                        <div className="mt-4 text-sm text-white/70">
                            Tracking #: <span className="text-white">{order.tracking_number}</span>{" "}
                            {order.carrier && <span className="text-white/55">via {order.carrier}</span>}
                        </div>
                    )}

                    <div className="mt-6 border-t border-[#27272A] pt-4">
                        <h3 className="font-display uppercase font-bold mb-2">Items</h3>
                        <div className="space-y-2">
                            {order.items.map((it, idx) => (
                                <div key={idx} className="flex gap-3 text-sm" data-testid={`track-item-${idx}`}>
                                    <div className="w-12 h-12 bg-[#0A0A0B] border border-[#27272A] overflow-hidden flex-shrink-0">
                                        {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="truncate">{it.name}</div>
                                        {it.variant_label && (
                                            <div className="text-[11px] uppercase tracking-widest text-white/55 font-display">
                                                {it.variant_label}
                                            </div>
                                        )}
                                        <div className="text-white/55 text-xs">× {it.quantity}</div>
                                    </div>
                                    <div>{formatPrice(it.subtotal || it.price * it.quantity, cur)}</div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-between mt-4 pt-3 border-t border-[#27272A] text-sm space-y-0">
                            <span className="text-white/65">Subtotal</span>
                            <span>{formatPrice(order.subtotal || 0, cur)}</span>
                        </div>
                        {order.discount_code && Number(order.discount_amount) > 0 && (
                            <div className="flex justify-between text-[#34C759] text-sm" data-testid="track-discount-row">
                                <span>Discount ({order.discount_code})</span>
                                <span>− {formatPrice(order.discount_amount, cur)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-sm">
                            <span className="text-white/65">Shipping</span>
                            <span>{Number(order.shipping || 0) === 0 ? "FREE" : formatPrice(order.shipping, cur)}</span>
                        </div>
                        <div className="flex justify-between mt-3 pt-3 border-t border-[#27272A]">
                            <span className="font-display uppercase font-bold">Total</span>
                            <span className="font-display font-black text-xl">{formatPrice(order.total, cur)}</span>
                        </div>
                        {order.payment_method && (
                            <div className="mt-3 text-xs text-white/65" data-testid="track-payment">
                                <span>Payment: </span>
                                <span className={`font-display uppercase tracking-widest font-bold ${order.payment_status === "paid" ? "text-[#34C759]" : "text-white"}`}>
                                    {order.payment_method === "razorpay"
                                        ? (order.payment_status === "paid" ? "Razorpay Paid" : `Razorpay (${order.payment_status})`)
                                        : order.payment_method === "cod"
                                          ? "Cash on Delivery"
                                          : order.payment_method}
                                </span>
                                {order.razorpay_payment_id && (
                                    <span className="block mt-0.5 text-[11px] text-white/45">
                                        Payment ID: {order.razorpay_payment_id}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
